import {
  Dropdown,
  Option,
  OptionGroup,
  Text,
  TextButton,
  TextField,
  TextWithLabel,
} from '@joor/design-system'
import { useSelector } from 'react-redux'

import { OrderStatus } from '__generated__/atlas-types'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'
import { isAccountTypeBrand as isAccountTypeBrandSelector } from 'store/currentUser/selectors'

import {
  BillingFormikErrors,
  HandleBillingChange,
} from '../../EditAddressesModal/hooks/useBillingForm'
import { useInputFieldKeyHandlers } from '../../EditAddressesModal/hooks/useInputFieldKeyHandlers'
import { CreateAddressLink } from '../CreateAddressLink/CreateAddressButton'
import {
  ClearFormButtonWrapper,
  Column,
  FieldsContainer,
  FormContainer,
  FormHeaderHolder,
  TextFieldGroup,
} from '../forms.ui'
import { UNITED_STATES_ID } from '../forms.utils'
import { BillingAddressFormValues, FieldIds } from './billingAddressForm.types'
import { messages } from 'features/Orders/Review/components/Overview/components/AddressesWidget/constants/messages'
import { AddressReviewPageIds } from 'features/Orders/Review/review.ids'
import { DropdownCallbacks } from 'features/Orders/Review/utils/formik.utils'

export enum BillingAddressFormTestIds {
  SelectAddressDropdown = 'billing-form-select-address-dropdown',
  CountryDropdown = 'billing-form-country-dropdown',
  PaymentMethod = 'billing-form-payment-method',
}

export type Props = {
  formId: string
  paymentMethodOptions?: Option[]
  orderStatus?: OrderStatus
  addressesOptionGroup: OptionGroup[]
  aggregatedOptions: Option[]
  onSelectAddress: ({ selected }: { selected: Option }) => void
  disableEditCompany?: boolean
  countryOptions?: Option[]
  currentTaxLabel?: string | null
  countryHandlers: DropdownCallbacks
  paymentMethodHandlers: DropdownCallbacks
  handleChange: HandleBillingChange
  values: BillingAddressFormValues
  errors: BillingFormikErrors
  resetForm: () => void
  retailerId?: string
  isRetailerRestrictedFromCreating?: boolean
  companyNameLabel: string
  readonly?: boolean
}

const TOOLTIP_INFO_TEXT = 'You are restricted from editing this information.'

export const BillingAddressForm = ({
  formId,
  paymentMethodOptions = [],
  orderStatus,
  addressesOptionGroup = [],
  aggregatedOptions = [],
  onSelectAddress,
  disableEditCompany,
  countryOptions = [],
  currentTaxLabel,
  countryHandlers,
  paymentMethodHandlers,
  handleChange,
  values,
  errors,
  resetForm,
  retailerId,
  isRetailerRestrictedFromCreating = false,
  companyNameLabel,
  readonly,
}: Props) => {
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)

  const { handleKeyDown } = useInputFieldKeyHandlers()

  return (
    <FormContainer id={formId}>
      <FormHeaderHolder>
        <div className="address-select">
          <Dropdown
            testId={BillingAddressFormTestIds.SelectAddressDropdown}
            id={FieldIds.AddressOption}
            label={messages.selectAddress}
            value={values[FieldIds.AddressOption]}
            options={addressesOptionGroup}
            placeholder={EMPTY_CELL_CONTENT}
            onSelect={onSelectAddress}
            errorMessage={errors[FieldIds.AddressOption]}
            isDisabled={!aggregatedOptions.length}
            isRequired
          />
        </div>
        {!readonly && (
          <CreateAddressLink
            retailerId={retailerId}
            isRetailerRestricted={isRetailerRestrictedFromCreating}
            createAddressId={AddressReviewPageIds.CreateAddressButtonBillingId}
          />
        )}
      </FormHeaderHolder>
      {!readonly && (
        <ClearFormButtonWrapper>
          <TextButton
            onClick={(e) => {
              e.preventDefault()
              resetForm()
            }}
          >
            {messages.clearAllFields}
          </TextButton>
        </ClearFormButtonWrapper>
      )}
      {!readonly ? (
        <FieldsContainer>
          <Column>
            {disableEditCompany ? (
              <TextWithLabel label={companyNameLabel}>
                <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                  {values[FieldIds.CompanyName] ?? EMPTY_CELL_CONTENT}
                </Text>
              </TextWithLabel>
            ) : (
              <TextField
                id={FieldIds.CompanyName}
                label={companyNameLabel}
                value={values[FieldIds.CompanyName] ?? EMPTY_CELL_CONTENT}
                onChange={handleChange}
                placeholder={EMPTY_CELL_CONTENT}
                errorMessage={errors[FieldIds.CompanyName]}
                onKeyDown={handleKeyDown}
                isRequired={!disableEditCompany}
              />
            )}
            <TextField
              id={FieldIds.Address1}
              label={messages.address1}
              value={values[FieldIds.Address1]}
              onChange={handleChange}
              placeholder={EMPTY_CELL_CONTENT}
              errorMessage={errors[FieldIds.Address1]}
              onKeyDown={handleKeyDown}
              isRequired
            />
            <TextField
              id={FieldIds.City}
              label={messages.city}
              value={values[FieldIds.City]}
              onChange={handleChange}
              placeholder={EMPTY_CELL_CONTENT}
              errorMessage={errors[FieldIds.City]}
              onKeyDown={handleKeyDown}
              isRequired
            />
            <Dropdown
              testId={BillingAddressFormTestIds.CountryDropdown}
              id={FieldIds.Country}
              label={messages.country}
              value={values[FieldIds.Country]}
              options={countryOptions}
              placeholder={EMPTY_CELL_CONTENT}
              errorMessage={errors[FieldIds.Country]}
              isDisabled={!countryOptions.length}
              menuPlacement="top"
              {...countryHandlers}
            />
            <TextField
              id={FieldIds.Phone}
              label={messages.phone}
              value={values[FieldIds.Phone]}
              onChange={handleChange}
              maxLength={20}
              placeholder="Phone"
              errorMessage={errors[FieldIds.Phone]}
              onKeyDown={handleKeyDown}
            />
          </Column>
          <Column>
            <TextFieldGroup>
              <TextWithLabel
                label={messages.billingCode}
                caption={messages.getBillingCodeCaption(isAccountTypeBrand)}
              >
                <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                  {values[FieldIds.BillingCode] ?? EMPTY_CELL_CONTENT}
                </Text>
              </TextWithLabel>
              <Dropdown
                testId={BillingAddressFormTestIds.PaymentMethod}
                id={FieldIds.PaymentMethod}
                label={messages.paymentMethod}
                options={paymentMethodOptions}
                value={values[FieldIds.PaymentMethod]}
                placeholder={EMPTY_CELL_CONTENT}
                isDisabled={
                  !paymentMethodOptions.length ||
                  (!isAccountTypeBrand && orderStatus !== OrderStatus.NOTES)
                }
                {...paymentMethodHandlers}
              />
            </TextFieldGroup>
            <TextField
              id={FieldIds.Address2}
              label={messages.address2}
              value={values[FieldIds.Address2]}
              onChange={handleChange}
              placeholder={EMPTY_CELL_CONTENT}
              onKeyDown={handleKeyDown}
            />
            <TextField
              id={FieldIds.Territory}
              label={
                values[FieldIds.Country]?.value === UNITED_STATES_ID
                  ? messages.state
                  : messages.territory
              }
              value={values[FieldIds.Territory]}
              onChange={handleChange}
              errorMessage={errors[FieldIds.Territory]}
              placeholder={EMPTY_CELL_CONTENT}
              onKeyDown={handleKeyDown}
            />
            <TextField
              id={FieldIds.PostalCode}
              label={
                values[FieldIds.Country]?.value === UNITED_STATES_ID
                  ? messages.zipCode
                  : messages.postalCode
              }
              value={values[FieldIds.PostalCode]}
              onChange={handleChange}
              placeholder={EMPTY_CELL_CONTENT}
              onKeyDown={handleKeyDown}
            />
            {!!currentTaxLabel && (
              <TextField
                id={FieldIds.TaxNumber}
                label={messages.getTaxLabel(currentTaxLabel)}
                value={values[FieldIds.TaxNumber]}
                onChange={handleChange}
                placeholder={EMPTY_CELL_CONTENT}
                onKeyDown={handleKeyDown}
              />
            )}
          </Column>
        </FieldsContainer>
      ) : (
        <FieldsContainer>
          <Column>
            <TextWithLabel label={companyNameLabel} caption={TOOLTIP_INFO_TEXT}>
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.CompanyName] ?? EMPTY_CELL_CONTENT}
              </Text>
            </TextWithLabel>
            <TextWithLabel
              label={messages.address1}
              caption={TOOLTIP_INFO_TEXT}
            >
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Address1]}
              </Text>
            </TextWithLabel>
            <TextWithLabel label={messages.city} caption={TOOLTIP_INFO_TEXT}>
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.City]}
              </Text>
            </TextWithLabel>
            <TextWithLabel label={messages.country} caption={TOOLTIP_INFO_TEXT}>
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Country]?.label}
              </Text>
            </TextWithLabel>
            <TextWithLabel label={messages.phone} caption={TOOLTIP_INFO_TEXT}>
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Phone]}
              </Text>
            </TextWithLabel>
          </Column>
          <Column>
            <TextFieldGroup>
              <TextWithLabel
                label={messages.billingCode}
                caption={TOOLTIP_INFO_TEXT}
              >
                <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                  {values[FieldIds.BillingCode] ?? EMPTY_CELL_CONTENT}
                </Text>
              </TextWithLabel>
              <Dropdown
                testId={BillingAddressFormTestIds.PaymentMethod}
                id={FieldIds.PaymentMethod}
                label={messages.paymentMethod}
                options={paymentMethodOptions}
                value={values[FieldIds.PaymentMethod]}
                placeholder={EMPTY_CELL_CONTENT}
                isDisabled={
                  !paymentMethodOptions.length ||
                  (!isAccountTypeBrand && orderStatus !== OrderStatus.NOTES)
                }
                {...paymentMethodHandlers}
              />
            </TextFieldGroup>
            <TextWithLabel
              label={messages.address2}
              caption={TOOLTIP_INFO_TEXT}
            >
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Address2]}
              </Text>
            </TextWithLabel>
            <TextWithLabel
              label={
                values[FieldIds.Country]?.value === UNITED_STATES_ID
                  ? messages.state
                  : messages.territory
              }
              caption={TOOLTIP_INFO_TEXT}
            >
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.Territory]}
              </Text>
            </TextWithLabel>
            <TextWithLabel
              label={
                values[FieldIds.Country]?.value === UNITED_STATES_ID
                  ? messages.zipCode
                  : messages.postalCode
              }
              caption={TOOLTIP_INFO_TEXT}
            >
              <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                {values[FieldIds.PostalCode]}
              </Text>
            </TextWithLabel>
            {!!currentTaxLabel && (
              <TextWithLabel
                label={messages.getTaxLabel(currentTaxLabel)}
                caption={TOOLTIP_INFO_TEXT}
              >
                <Text fontFamily="alpha" fontSize="small" fontWeight="medium">
                  {values[FieldIds.TaxNumber]}
                </Text>
              </TextWithLabel>
            )}
          </Column>
        </FieldsContainer>
      )}
    </FormContainer>
  )
}
